import React, { forwardRef, useId } from 'react';
import { Check } from 'lucide-react';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  helpText?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, helpText, className = '', id, ...props }, ref) => {
    const autoId = useId();
    const checkboxId = id || autoId;

    return (
      <div className="w-full">
        <label
          htmlFor={checkboxId}
          className={`
            flex items-start gap-3 cursor-pointer group
            ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              className="sr-only peer"
              {...props}
            />
            <div
              className={`
                w-5 h-5
                border-2
                ${error ? 'border-red-500' : 'border-neutral-600'}
                rounded-md
                bg-neutral-900/50
                transition-all duration-200
                peer-checked:bg-primary-500
                peer-checked:border-primary-500
                peer-focus:ring-2
                peer-focus:ring-primary-500/20
                group-hover:border-neutral-500
                peer-checked:group-hover:border-primary-400
              `}
            />
            <Check className="absolute inset-0 m-auto w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
          </div>

          <div className="flex-1">
            {label && (
              <span className="text-sm text-neutral-200">
                {label}
                {props.required && <span className="text-red-400 ml-1">*</span>}
              </span>
            )}

            {helpText && !error && (
              <p className="mt-0.5 text-xs text-neutral-500">{helpText}</p>
            )}

            {error && (
              <p className="mt-0.5 text-xs text-red-400">{error}</p>
            )}
          </div>
        </label>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
